import { Checkbox } from "@kobalte/core/checkbox";
import { Dialog } from "@kobalte/core/dialog";
import { ICON_MAPPING } from "@storytell/bsky-icons";
import { SANCTIONED_ICONS, getIconComponent } from "@storytell/bsky-icons/src/iconMapping";
import { Icons } from "@storytell/cp-client";
import type { Label } from "@storytell/types";
import { AnimateChildren } from "@storytell/ui/animation";
import { StButton } from "@storytell/ui/buttons";
import { StPopover, StPopoverTrigger } from "@storytell/ui/popover";
import { TbCheck, TbHelpCircle, TbPlus, TbX } from "solid-icons/tb";
import {
  type Component,
  For,
  type Setter,
  Show,
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
  onMount,
} from "solid-js";
import { createStore } from "solid-js/store";
import { Dynamic } from "solid-js/web";
import { useWire } from "~/wire";
import { isLightColor } from "../layouts/minimal/isLightColor";

const SANCTIONED_COLORS = [
  ["#ffa2a2", "#ff6467", "#fb2c36", "#e7000b", "#c10007"],
  ["#ffdf20", "#fdc700", "#f0b100", "#d08700", "#a65f00"],
  ["#bbf451", "#9ae600", "#7ccf00", "#5ea500", "#497d00"],
  ["#8ec5ff", "#51a2ff", "#2b7fff", "#155dfc", "#1447e6"],
  ["#c4b4ff", "#a684ff", "#8e51ff", "#7f22fe", "#7008e7"],
  ["#fda5d5", "#fb64b6", "#f6339a", "#e60076", "#c6005c"],
];

export const LabelPicker: Component<{
  mount?: Node;
  search: string;
  setSearch: Setter<string>;
  labels: Label[];
  selectedLabels: string[];
  partialLabels?: string[];
  disableCreate?: boolean;
  title?: string;
  onSelectLabel: (label: Label) => void;
  onDeselectLabel: (label: Label) => void;
  onCreateLabel: (label: {
    name: string;
    description: string;
    icon: Icons.Icon;
    color: string;
  }) => Promise<void>;
  onClose?: () => void;
}> = (props) => {
  const wire = useWire();
  const [isOpen, setIsOpen] = createSignal(false);
  const [iconSearch, setIconSearch] = createSignal("");
  const [createLabel, setCreateLabel] = createStore({
    loading: false,
    name: props.search || "",
    description: "",
    icon: Icons.Icon.FILE,
    color: SANCTIONED_COLORS[0][2],
  });

  // Helper function to get icon name for search
  const getIconName = (icon: Icons.Icon) => {
    const iconKey = Object.keys(Icons.Icon).find((key) => Icons.Icon[key as keyof typeof Icons.Icon] === icon);
    return iconKey?.replace(/_/g, " ").toLowerCase() || "";
  };

  // Filter icons based on search term
  const filteredIcons = createMemo(() => {
    const searchTerm = iconSearch().toLowerCase().trim();
    if (!searchTerm) return SANCTIONED_ICONS.filter((icon) => !!ICON_MAPPING[icon as Icons.Icon]);

    return SANCTIONED_ICONS.filter((icon) => {
      if (!ICON_MAPPING[icon as Icons.Icon]) return false;
      const iconName = getIconName(icon);

      // First try exact substring match (fastest)
      if (iconName.includes(searchTerm)) return true;

      // Then try flexible search: check if all characters in search term appear in order
      const searchChars = searchTerm.split("");
      let iconIndex = 0;

      for (const char of searchChars) {
        const foundIndex = iconName.indexOf(char, iconIndex);
        if (foundIndex === -1) return false;
        iconIndex = foundIndex + 1;
      }

      return true;
    });
  });

  return (
    <div class="p-2 pb-3">
      <div class="flex items-start gap-2 mb-3 justify-between">
        <h3>{props.title ?? "Add a label"}</h3>
        <Show when={props.onClose}>
          <StButton icon={TbX} iconClass="size-4" noBorder onClick={props.onClose} class="shrink-0" />
        </Show>
      </div>

      <input
        value={props.search}
        onInput={(e) => props.setSearch(e.currentTarget.value)}
        type="text"
        placeholder="Search for a label"
        class="p-2 min-w-52 border w-full border-primary-border rounded-lg mb-4 outline-none"
      />

      <div class="flex flex-col space-y-4 max-h-96 overflow-y-auto mb-4">
        <For each={props.labels.toSorted((a, b) => a.name.localeCompare(b.name))}>
          {(label) => {
            const isChecked = () => !!props.selectedLabels.find((l) => l === label.id);
            const isPartial = () => !!props.partialLabels?.find((l) => l === label.id);
            return (
              <div class="flex items-center gap-2">
                <Checkbox
                  checked={isChecked()}
                  indeterminate={isPartial()}
                  aria-label={`Select ${label.name}`}
                  onChange={(val) => {
                    if (val) {
                      props.onSelectLabel(label);
                    } else {
                      props.onDeselectLabel(label);
                    }
                  }}
                  class="flex items-center justify-center mr-2"
                >
                  <Checkbox.Input />
                  <Checkbox.Control
                    data-some-selected={isPartial()}
                    class="w-4 h-4 border border-primary-border dark:border-white/30 rounded bg-tile data-[some-selected=true]:bg-black/30 dark:data-[some-selected=true]:bg-white/30 data-[checked]:bg-brand data-[checked]:border-brand flex items-center justify-center transition-colors"
                  >
                    <Show when={isChecked() || isPartial()}>
                      <Checkbox.Indicator as={TbCheck} class="w-4 h-4 text-on-brand stroke-3" />
                    </Show>
                  </Checkbox.Control>
                </Checkbox>
                <span
                  style={{
                    "--label-color": label.color,
                    "--label-icon": isLightColor(label.color) ? "black" : "white",
                  }}
                >
                  <Dynamic
                    component={getIconComponent(label.icon)}
                    class="size-5 p-1 rounded-full bg-[var(--label-color)] text-[var(--label-icon)]"
                  />
                </span>

                <button
                  type="button"
                  onClick={() => {
                    if (isChecked()) {
                      props.onDeselectLabel(label);
                    } else {
                      props.onSelectLabel(label);
                    }
                  }}
                  class="text-sm max-w-60 line-clamp-2 text-left hover:bg-tile-hover rounded px-1 py-0.5 -mx-1 -my-0.5 transition-colors cursor-pointer"
                >
                  {label.name}
                </button>
              </div>
            );
          }}
        </For>
      </div>

      <Show when={!props.disableCreate}>
        <AnimateChildren show={isOpen()} enter={{ opacity: [0, 1], y: [10, 0] }} exit={{ opacity: [1, 0], y: [0, 10] }}>
          {(anim) => (
            <Dialog
              open={anim.isMounted()}
              onOpenChange={(val) => {
                if (!val) setIsOpen(false);
              }}
            >
              <Dialog.Portal mount={props.mount}>
                <Dialog.Overlay class="fixed z-90 inset-0 bg-black/50 dark:bg-white/10" />
                <div ref={anim.ref} class="fixed z-90 inset-0 flex items-center justify-center">
                  <Dialog.Content class="max-h-[80vh] relative w-screen text-primary-text max-w-xl bg-panel overflow-y-auto rounded-xl p-8">
                    <div class="flex items-start gap-2 mb-4 justify-between">
                      <h3 class="text-lg font-medium">Create a new Label</h3>
                      <div class="flex items-center gap-3">
                        <a
                          href="https://go.storytell.ai/learn-labels"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="inline-flex items-center gap-1 underline text-brand text-xs"
                        >
                          <TbHelpCircle />
                          Learn about Labels
                        </a>
                        <Dialog.CloseButton as={StButton} class="shrink-0">
                          <TbX />
                        </Dialog.CloseButton>
                      </div>
                    </div>

                    <div class="flex flex-col gap-3 w-full mb-4">
                      <div class="flex flex-col gap-2">
                        <StPopover
                          placement="bottom-start"
                          portalProps={{ mount: props.mount }}
                          contentProps={{ class: "z-90" }}
                          content={
                            <div class="grid grid-cols-2 gap-2 p-2">
                              <div>
                                <p class="mb-2">Color</p>
                                <div class="grid gap-2 grid-cols-5 max-h-40 overflow-y-auto p-0.5">
                                  <For each={SANCTIONED_COLORS.flat()}>
                                    {(color) => (
                                      <button
                                        type="button"
                                        data-active={createLabel.color === color}
                                        onClick={() => setCreateLabel("color", color)}
                                        class="size-7 rounded-lg cursor-pointer data-[active=true]:ring-2 data-[active=true]:ring-primary-text"
                                        style={{ "background-color": color }}
                                      />
                                    )}
                                  </For>
                                </div>
                              </div>
                              <div>
                                <p class="mb-2">Icon</p>
                                <input
                                  type="text"
                                  placeholder="Search icons..."
                                  value={iconSearch()}
                                  onInput={(e) => setIconSearch(e.currentTarget.value)}
                                  class="w-full p-2 border border-primary-border rounded-lg mb-2 outline-none text-sm min-w-0"
                                />
                                <div class="flex flex-wrap max-w-52 gap-4 max-h-40 overflow-y-auto p-0.5">
                                  <For each={filteredIcons()}>
                                    {(icon) => (
                                      <button
                                        type="button"
                                        data-active={createLabel.icon === icon}
                                        onClick={() => setCreateLabel("icon", icon as Icons.Icon)}
                                        class="text-primary-text grid place-content-center hover:bg-tile-hover cursor-pointer rounded p-1 transition-colors data-[active=true]:ring-2 data-[active=true]:ring-brand"
                                        title={getIconName(icon)}
                                      >
                                        <Dynamic component={ICON_MAPPING[icon as Icons.Icon]} class="size-5" />
                                      </button>
                                    )}
                                  </For>
                                </div>
                              </div>
                            </div>
                          }
                        >
                          <StPopoverTrigger class="cursor-pointer rounded-lg border-2 border-dashed border-primary-border hover:border-brand hover:bg-brand/5 transition-all duration-200 p-3 flex items-center gap-3 w-fit">
                            <div
                              class="rounded-full size-10 shrink-0 grid place-content-center ring-2 ring-white dark:ring-gray-900 shadow-sm"
                              style={{
                                "background-color": createLabel.color,
                                color: isLightColor(createLabel.color) ? "black" : "white",
                              }}
                            >
                              <Dynamic component={ICON_MAPPING[createLabel.icon]} class="size-6" />
                            </div>
                            <div class="flex flex-col items-start">
                              <span class="text-sm font-medium">
                                <Show when={createLabel.name.trim()} fallback="Customize appearance">
                                  {createLabel.name.trim()}
                                </Show>
                              </span>
                              <span class="text-xs text-secondary-text">
                                <Show when={createLabel.name.trim()} fallback="Click to change color and icon">
                                  {createLabel.description.trim()}
                                </Show>
                              </span>
                            </div>
                          </StPopoverTrigger>
                        </StPopover>
                      </div>

                      <div class="flex flex-col gap-2">
                        <label class="text-sm font-medium text-secondary-text">Label Name</label>
                        <input
                          required
                          type="text"
                          value={props.search}
                          onInput={(e) => setCreateLabel("name", e.currentTarget.value)}
                          placeholder="Name"
                          class="w-full p-2 border border-primary-border rounded-lg outline-none"
                        />
                      </div>
                    </div>
                    <label class="flex flex-col gap-2">
                      <span class="text-sm text-secondary-text ml-1">Description</span>
                      <textarea
                        onInput={(e) => setCreateLabel("description", e.currentTarget.value)}
                        placeholder="My Label"
                        class="w-full p-2 border border-primary-border rounded-lg mb-4 outline-none"
                      />
                    </label>

                    <StButton
                      loading={createLabel.loading}
                      disabled={createLabel.loading}
                      onClick={() => {
                        setCreateLabel("loading", true);
                        props
                          .onCreateLabel({ ...createLabel, name: createLabel.name.trim() || props.search.trim() })
                          .finally(() => setCreateLabel("loading", false));
                      }}
                      icon={TbPlus}
                      class="ml-auto bg-brand hover:bg-brand-hover text-on-brand px-3 py-2"
                    >
                      Create Label
                    </StButton>
                  </Dialog.Content>
                </div>
              </Dialog.Portal>
            </Dialog>
          )}
        </AnimateChildren>
        <Show when={wire.grants.canCreate("cp.labels")}>
          <StButton icon={TbPlus} onClick={() => setIsOpen(true)} iconClass="size-4" class="w-full">
            Create a new Label
          </StButton>
        </Show>
      </Show>

      <div class="flex justify-end mt-3">
        <a
          href="https://go.storytell.ai/learn-labels"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center gap-1 underline text-brand text-xs"
        >
          <TbHelpCircle class="size-4" />
          Learn about Labels
        </a>
      </div>
    </div>
  );
};
